import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import ClientLayout from '../../components/ClientLayout';
import { publicExpertApi, clientPortalApi, getErrorMessage } from '../../lib/http';
import type {
  ExpertPageProfile,
  ExpertService,
  AvailabilityDay,
  AvailabilityWeek,
  AvailabilityMonth,
} from '../../types';
import PageHeader from '../../components/client/PageHeader';
import { ExpertHeader } from '../../components/expert/ExpertHeader';
import { ServiceList } from '../../components/expert/ServiceList';
import { CalendarModeToggle } from '../../components/expert/CalendarModeToggle';
import { CalendarNavigator } from '../../components/expert/CalendarNavigator';
import { CalendarDay, CalendarWeek, CalendarMonth } from '../../components/expert/CalendarViews';
import { BookingModal } from '../../components/expert/BookingModal';
import UpcomingBookingsSection from '../../components/expert/UpcomingBookingsSection';

type CalendarMode = 'day' | 'week' | 'month';

export const ExpertPage: React.FC = () => {
  const { expertSlug } = useParams<{ expertSlug: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Data state
  const [profile, setProfile] = useState<ExpertPageProfile | null>(null);
  const [services, setServices] = useState<ExpertService[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const [calendarMode, setCalendarMode] = useState<CalendarMode>('month');
  const [anchorDate, setAnchorDate] = useState<Date>(new Date());
  const [availability, setAvailability] = useState<
    AvailabilityDay | AvailabilityWeek | AvailabilityMonth | null
  >(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  // Load expert profile and services on mount
  useEffect(() => {
    if (!expertSlug) return;

    const loadExpertData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [profileRes, servicesRes] = await Promise.all([
          publicExpertApi.getProfile(expertSlug),
          publicExpertApi.getServices(expertSlug, { active_only: true }),
        ]);

        setProfile(profileRes.data);
        setServices(servicesRes.data.services);

        // Auto-select if single service
        if (servicesRes.data.services.length === 1) {
          setSelectedServiceId(servicesRes.data.services[0].id);
        }
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    loadExpertData();
  }, [expertSlug]);

  // Load availability when service, mode, or anchor date changes
  useEffect(() => {
    if (!expertSlug || !selectedServiceId) {
      setAvailability(null);
      return;
    }

    const loadAvailability = async () => {
      try {
        setAvailabilityLoading(true);

        const dateStr = `${anchorDate.getFullYear()}-${String(anchorDate.getMonth() + 1).padStart(2, '0')}-${String(anchorDate.getDate()).padStart(2, '0')}`;

        const res = await publicExpertApi.getAvailability(expertSlug, {
          service_id: selectedServiceId,
          mode: calendarMode,
          date: dateStr,
        });

        setAvailability(res.data);
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setAvailabilityLoading(false);
      }
    };

    loadAvailability();
  }, [expertSlug, selectedServiceId, calendarMode, anchorDate]);

  const handleServiceSelect = (serviceId: string) => {
    setSelectedServiceId(serviceId);
    setSelectedSlot(null);
    setCalendarMode('month'); // Reset to month view on service change
  };

  const handleModeChange = (mode: CalendarMode) => {
    setCalendarMode(mode);
    setSelectedSlot(null);

    // // Reset to today when switching to day or week mode
    // if (mode === 'day' || mode === 'week') {
    //   setAnchorDate(new Date());
    // }
  };

  const handleNavigate = (direction: 'prev' | 'next') => {
    const newDate = new Date(anchorDate);

    if (calendarMode === 'day') {
      newDate.setDate(anchorDate.getDate() + (direction === 'next' ? 1 : -1));
    } else if (calendarMode === 'week') {
      newDate.setDate(anchorDate.getDate() + (direction === 'next' ? 7 : -7));
    } else if (calendarMode === 'month') {
      newDate.setMonth(anchorDate.getMonth() + (direction === 'next' ? 1 : -1));
    }

    setAnchorDate(newDate);
    setSelectedSlot(null);
  };

  const handleDateClick = (date: string) => {
    // Month view: drill down to day
    setAnchorDate(new Date(date));
    setCalendarMode('day');
    setSelectedSlot(null);
  };

  const handleSlotClick = (slot: string) => {
    setSelectedSlot(slot);
    setBookingError(null);
    setIsModalOpen(true);
  };

  const handleBookingConfirm = async (notes: string) => {
    if (!expertSlug || !selectedServiceId || !selectedSlot) return;

    try {
      setBookingError(null);

      await clientPortalApi.bookings.create({
        expert_slug: expertSlug,
        service_id: selectedServiceId,
        slot_start: selectedSlot,
        notes: notes.trim() || undefined,
      });

      // Invalidate bookings cache to refresh the data
      queryClient.invalidateQueries({ queryKey: ['client-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['client-bookings-preview'] });
      queryClient.invalidateQueries({ queryKey: ['client-bookings-expert', expertSlug] });

      // Success! Show toast and redirect to client home
      toast.success('Booking confirmed successfully!');
      setTimeout(() => {
        navigate('/u');
      }, 500);
    } catch (err: any) {
      if (err.response?.status === 409) {
        setBookingError('This time slot has already been booked. Please select another time.');

        // Refresh availability
        if (selectedServiceId) {
          const dateStr = `${anchorDate.getFullYear()}-${String(anchorDate.getMonth() + 1).padStart(2, '0')}-${String(anchorDate.getDate()).padStart(2, '0')}`;
          const res = await publicExpertApi.getAvailability(expertSlug, {
            service_id: selectedServiceId,
            mode: calendarMode,
            date: dateStr,
          });
          setAvailability(res.data);
        }
      } else if (err.response?.status === 401) {
        setBookingError('Please sign in to book an appointment.');
        setTimeout(() => {
          navigate('/client/login');
        }, 2000);
      } else if (err.response?.status === 422) {
        const apiError = err.response?.data?.error;
        setBookingError(apiError?.message || 'Booking policy violation. Please try another time.');
      } else {
        setBookingError(getErrorMessage(err));
      }
    }
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          <div className="animate-pulse space-y-6">
            <div className="h-32 bg-gray-200 rounded-lg"></div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
            <div className="h-96 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </ClientLayout>
    );
  }

  if (!profile || error) {
    return (
      <ClientLayout>
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg">
            <p className="font-semibold">{error || 'Expert not found'}</p>
            <button
              onClick={() => navigate('/u')}
              className="mt-4 text-sm text-red-600 hover:text-red-800 underline"
            >
              ← Back to home
            </button>
          </div>
        </div>
      </ClientLayout>
    );
  }

  const selectedService = services.find((s) => s.id === selectedServiceId);

  return (
    <ClientLayout>
      <div className="w-full">
        {/* Page Header */}
        <PageHeader title={profile.display_name} backTo="/u" />

        {/* Expert Header */}
        <ExpertHeader profile={profile} />

        {/* Upcoming Bookings with this Expert */}
        {expertSlug && (
          <UpcomingBookingsSection
            expertSlug={expertSlug}
            expertName={profile.display_name}
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Services List */}
          <div className="lg:col-span-1">
            <ServiceList
              services={services}
              selectedServiceId={selectedServiceId}
              onSelectService={handleServiceSelect}
            />
          </div>

          {/* Calendar & Booking */}
          <div className="lg:col-span-2">
            {!selectedServiceId ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-gray-500 text-lg">Select a service to view availability</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                {/* Calendar Controls */}
                <div className="flex items-center justify-center mb-6">
                  <CalendarModeToggle mode={calendarMode} onChange={handleModeChange} />
                </div>

                <CalendarNavigator
                  mode={calendarMode}
                  anchorDate={anchorDate}
                  onNavigate={handleNavigate}
                />

                {/* Calendar Content */}
                {availabilityLoading ? (
                  <div className="py-12 text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                    <p className="text-gray-500 mt-4">Loading availability...</p>
                  </div>
                ) : availability ? (
                  <div className="mt-6">
                    {availability.mode === 'day' && (
                      <CalendarDay
                        data={availability as AvailabilityDay}
                        selectedSlot={selectedSlot}
                        onSlotClick={handleSlotClick}
                      />
                    )}
                    {availability.mode === 'week' && (
                      <CalendarWeek
                        data={availability as AvailabilityWeek}
                        selectedSlot={selectedSlot}
                        onSlotClick={handleSlotClick}
                      />
                    )}
                    {availability.mode === 'month' && (
                      <CalendarMonth
                        data={availability as AvailabilityMonth}
                        onDateClick={handleDateClick}
                      />
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* Booking Modal */}
        {selectedService && selectedSlot && (
          <BookingModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setBookingError(null);
            }}
            expertName={profile.display_name}
            service={selectedService}
            slotStart={selectedSlot}
            onConfirm={handleBookingConfirm}
            error={bookingError}
            timezone={profile.timezone}
          />
        )}
      </div>
    </ClientLayout>
  );
};
